import { Monitor, CheckCircle, XCircle, Cpu, HardDrive, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { DeviceFleetStatus } from '../../services/dashboardSummary.service';

interface FleetStatusCardProps {
    data: DeviceFleetStatus | null;
    loading: boolean;
}

type HealthStatus = 'healthy' | 'warning' | 'critical' | 'offline';

// Display labels aligned with /monitoring/status (Online | Warning | Error | Offline)
// so users see the same vocabulary on both pages.
const HEALTH_LABEL: Record<HealthStatus, string> = {
    healthy: 'Online',
    warning: 'Warning',
    critical: 'Error',
    offline: 'Offline',
};

// Health status dot
const HealthDot = ({ status }: { status: HealthStatus }) => {
    const colors: Record<HealthStatus, string> = {
        healthy: 'bg-success-500',
        warning: 'bg-warning-500',
        critical: 'bg-danger-500',
        offline: 'bg-gray-400',
    };

    return (
        <div
            className={`w-2.5 h-2.5 rounded-full ${colors[status]} ${status !== 'offline' ? 'animate-pulse' : ''}`}
            title={HEALTH_LABEL[status]}
        />
    );
};

// Determine health status based on CPU/Memory
const getHealthStatus = (device: DeviceFleetStatus['devices'][0]): HealthStatus => {
    if (device.status === 'offline') return 'offline';
    const cpu = device.cpu || 0;
    const memory = device.memory || 0;
    if (cpu > 90 || memory > 90) return 'critical';
    if (cpu > 70 || memory > 70) return 'warning';
    return 'healthy';
};

export const FleetStatusCard = ({ data, loading }: FleetStatusCardProps) => {
    if (loading) {
        return (
            <div className="card animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
            </div>
        );
    }

    if (!data || data.totalDevices === 0) {
        return (
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Edge Fleet</h2>
                    <Monitor className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Monitor className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No Edges registered</p>
                    <p className="text-xs text-gray-400 mt-1">Add edge devices to monitor fleet</p>
                </div>
            </div>
        );
    }

    const onlinePercent = (data.onlineDevices / data.totalDevices) * 100;

    // Count devices by health status
    const healthCounts = data.devices.reduce(
        (acc, device) => {
            const status = getHealthStatus(device);
            acc[status]++;
            return acc;
        },
        { healthy: 0, warning: 0, critical: 0, offline: 0 }
    );

    return (
        <div className="card">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Edge Fleet</h2>
                <Link
                    to="/monitoring/status"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                    View All →
                </Link>
            </div>

            {/* Status Summary */}
            <div className="flex items-center space-x-6 mb-4">
                <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-success-500" />
                    <span className="text-2xl font-bold text-gray-900">{data.onlineDevices}</span>
                    <span className="text-sm text-gray-500">Online</span>
                </div>
                <div className="flex items-center space-x-2">
                    <XCircle className="w-5 h-5 text-danger-500" />
                    <span className="text-2xl font-bold text-gray-900">{data.offlineDevices}</span>
                    <span className="text-sm text-gray-500">Offline</span>
                </div>
            </div>

            {/* Health Status Grid */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Health Status</span>
                    <Activity className="w-4 h-4 text-gray-400" />
                </div>
                <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1.5">
                        <HealthDot status="healthy" />
                        <span className="text-xs text-gray-500">Online</span>
                        <span className="text-sm font-medium text-gray-700">{healthCounts.healthy}</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <HealthDot status="warning" />
                        <span className="text-xs text-gray-500">Warning</span>
                        <span className="text-sm font-medium text-gray-700">{healthCounts.warning}</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <HealthDot status="critical" />
                        <span className="text-xs text-gray-500">Error</span>
                        <span className="text-sm font-medium text-gray-700">{healthCounts.critical}</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <HealthDot status="offline" />
                        <span className="text-xs text-gray-500">Offline</span>
                        <span className="text-sm font-medium text-gray-700">{healthCounts.offline}</span>
                    </div>
                </div>
            </div>

            {/* Availability Bar */}
            <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">Fleet Availability</span>
                    <span className="font-semibold text-gray-900">{onlinePercent.toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                        className={`h-2 rounded-full transition-all ${onlinePercent >= 90 ? 'bg-success-500' :
                            onlinePercent >= 70 ? 'bg-warning-500' : 'bg-danger-500'
                            }`}
                        style={{ width: `${onlinePercent}%` }}
                    />
                </div>
            </div>

            {/* Resource Stats */}
            <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                        <Cpu className="w-4 h-4 text-primary-500" />
                        <span className="text-xs text-gray-500">Avg CPU</span>
                        <span className="ml-auto text-sm font-semibold text-gray-900">
                            {data.avgCpu > 0 ? `${data.avgCpu.toFixed(1)}%` : 'N/A'}
                        </span>
                    </div>
                    {data.avgCpu > 0 && (
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                                className={`h-1.5 rounded-full transition-all ${
                                    data.avgCpu > 90 ? 'bg-danger-500' :
                                    data.avgCpu > 70 ? 'bg-warning-500' : 'bg-primary-500'
                                }`}
                                style={{ width: `${Math.min(data.avgCpu, 100)}%` }}
                            />
                        </div>
                    )}
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                        <HardDrive className="w-4 h-4 text-info-500" />
                        <span className="text-xs text-gray-500">Avg Memory</span>
                        <span className="ml-auto text-sm font-semibold text-gray-900">
                            {data.avgMemory > 0 ? `${data.avgMemory.toFixed(1)}%` : 'N/A'}
                        </span>
                    </div>
                    {data.avgMemory > 0 && (
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                                className={`h-1.5 rounded-full transition-all ${
                                    data.avgMemory > 90 ? 'bg-danger-500' :
                                    data.avgMemory > 70 ? 'bg-warning-500' : 'bg-info-500'
                                }`}
                                style={{ width: `${Math.min(data.avgMemory, 100)}%` }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FleetStatusCard;
